<template>
  <v-wait for="isFetchingDashboard">
    <TitleBar />
    <core-spinner slot="waiting" />
    <div class="grid-container">
      <SupplierList
        :items="suppliers"
        :selected-item="selectedMixedSupplierId"
        class="supplier-list"
        @onSupplierSelected="onMixedSupplierChange"
      />
      <template v-if="isConcreteSelected">
        <ConcreteForemanList
          :items="concreteForemanTableList"
          :selected-formula="selectedConcreteFormulaId"
          class="mixed-mix-aggregate-list"
          @onItemSelected="onFormulaSelected"
        />
        <VariationChart
          id="concreteVariation"
          class="variation-chart"
          :data="concreteVariationsData"
          :title="$t('views.dashboard.variation-chart.title')"
          translation-path="views.dashboard.concrete.params"
          @rowclick="onVariationRowClick"
        />
        <LineBoundaryChart
          id="concreteBoundary"
          class="boundary-chart"
          :show-details-action="false"
          :title="$t('views.dashboard.concrete.illustrated')"
          :subtitle="$t('views.dashboard.concrete.params.' + selectedParam)"
          :boundary-data="concreteTimeline.deviations"
          :upper-h-r="concreteTimeline.upperHR"
          :upper-a-r="concreteTimeline.upperAR"
          :upper-l-r="concreteTimeline.upperLR"
          :zero-r="0"
          :lower-l-r="concreteTimeline.lowerLR"
          :lower-a-r="concreteTimeline.lowerAR"
          :lower-h-r="concreteTimeline.lowerHR"
        />
      </template>
      <template v-else>
        <MixAggregateList
          :items="mixedMaterials"
          :selected-item="selectedMaterial"
          :selected-item-type="selectedMaterialType"
          class="mixed-mix-aggregate-list"
          @onItemSelected="onSelectedMaterialChange"
        />
        <VariationChart
          id="variation-chart"
          class="variation-chart"
          :data="mixedSampleProperties"
          :title="$t('views.dashboard.variation-chart.title')"
          :translation-path="variationTranslationPath"
          @rowclick="onPropertyRowClick($event)"
        />
        <LineBoundaryChart
          id="RawMaterialsPropertyChart"
          :title="$t('views.dashboard.boundary-chart.title')"
          :subtitle="$t(mixedpropertyName)"
          :boundary-data="mixedpropertyTimeline"
          :upper-h-r="mixedSpecifications[6] - mixedSpecifications[3]"
          :upper-a-r="mixedSpecifications[5] - mixedSpecifications[3]"
          :upper-l-r="mixedSpecifications[4] - mixedSpecifications[3]"
          :zero-r="0"
          :lower-l-r="mixedSpecifications[2] - mixedSpecifications[3]"
          :lower-a-r="mixedSpecifications[1] - mixedSpecifications[3]"
          :lower-h-r="mixedSpecifications[0] - mixedSpecifications[3]"
          class="boundary-chart"
        />
      </template>
    </div>
  </v-wait>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex'
import { references as referenceTypes } from '@/utils/enum'
import { defaultSpecificationRange } from '@/utils/constants'
import { concreteVariations, paramTimeline } from '@/utils/concrete'
import MixAggregateList from './MixAggregateList.vue'
import SupplierList from './SupplierList.vue'
import ConcreteForemanList from './ConcreteForemanList.vue'
import TitleBar from '@/components/core/TitleBar.vue'
import VariationChart from '../Charts/Variation.vue'
import LineBoundaryChart from '../Charts/Lines/Boundary.vue'
import productionWeatherMixin from '@/mixins/productionWeatherMixin'

export default {
  components: {
    LineBoundaryChart,
    MixAggregateList,
    SupplierList,
    ConcreteForemanList,
    TitleBar,
    VariationChart,
  },
  mixins: [productionWeatherMixin],
  props: {
    mixDesign: {
      type: String,
      default: '',
    },
    supplier: {
      type: String,
      default: '',
    },
    specification: {
      type: String,
      default: '',
    },
    stockpile: {
      type: String,
      default: '',
    },
    foremanType: {
      type: String,
      default: 'superintendent',
    },
  },

  data() {
    return {
      selectedParam: 'air',
    }
  },

  computed: {
    ...mapState('production', [
      'aggregates',
      'asphaltSamples',
      'mixDesigns',
      'mixedSuppliers',
      'selectedAggregateSpecification',
      'selectedAggregateSampleProperty',
      'selectedAggregateStockpile',
      'selectedMixedSupplierId',
      'selectedMixId',
      'selectedSamplePropertyId',
      'concreteSuppliers',
      'selectedConcreteSupplierId',
      'selectedConcreteFormulaId',
    ]),
    ...mapGetters('production', [
      'aggregateSampleProperties',
      'aggregateSamplePropertyTimeline',
      'aggregateTableList',
      'asphaltSampleProperties',
      'asphaltSamplePropertyTimeline',
      'mixDesignTableList',
      'selectedAggregate',
      'selectedMixDesign',
      'selectedMixedSupplier',
      'concreteForemanTableList',
      'selectedConcreteFormula',
      'selectedConcreteSupplier',
    ]),
    suppliers() {
      const concrete = this.concreteSuppliers.map((supplier) => ({
        ...supplier,
        type: referenceTypes.CONCRETE,
      }))
      if (this.foremanType === referenceTypes.CONCRETE) return concrete
      if (this.foremanType === 'superintendent') {
        return [...this.mixedSuppliers, ...concrete]
      }
      return this.mixedSuppliers
    },
    selectedSupplier() {
      return this.suppliers.find(
        (supplier) => supplier._id === this.selectedMixedSupplierId,
      )
    },
    selectedSupplierType() {
      return this.selectedSupplier?.type
    },
    isConcreteSelected() {
      return this.selectedSupplierType === referenceTypes.CONCRETE
    },
    mixedMaterials() {
      if (this.selectedSupplierType === referenceTypes.SUPPLIER) {
        return this.aggregateTableList
      } else if (this.selectedSupplierType === referenceTypes.PLANT) {
        return this.mixDesignTableList
      } else {
        return []
      }
    },
    selectedMaterial() {
      if (this.selectedSupplierType === referenceTypes.SUPPLIER) {
        return (
          this.selectedAggregateSpecification +
          '-' +
          this.selectedAggregateStockpile
        )
      } else if (this.selectedSupplierType === referenceTypes.PLANT) {
        return this.selectedMixId
      } else {
        return ''
      }
    },
    selectedMaterialType() {
      if (this.selectedSupplierType === referenceTypes.SUPPLIER) {
        return referenceTypes.LAB_SPEC_MATERIAL
      } else if (this.selectedSupplierType === referenceTypes.PLANT) {
        return referenceTypes.LAB_MIX_DESIGN
      } else {
        return ''
      }
    },
    mixedSpecifications() {
      if (this.selectedSupplierType === referenceTypes.SUPPLIER) {
        return this.selectedAggregate &&
          this.selectedAggregate.specifications &&
          this.selectedAggregateSampleProperty
          ? this.selectedAggregate.specifications[
              this.selectedAggregateSampleProperty
            ]
          : defaultSpecificationRange
      } else if (this.selectedSupplierType === referenceTypes.PLANT) {
        return (
          this.selectedMixDesign?.specifications[
            this.selectedSamplePropertyId
          ] || defaultSpecificationRange
        )
      } else {
        return defaultSpecificationRange
      }
    },
    mixedpropertyTimeline() {
      if (this.selectedSupplierType === referenceTypes.SUPPLIER) {
        return this.aggregateSamplePropertyTimeline
      } else if (this.selectedSupplierType === referenceTypes.PLANT) {
        return this.asphaltSamplePropertyTimeline
      } else {
        return []
      }
    },
    mixedpropertyName() {
      if (
        this.selectedSupplierType === referenceTypes.SUPPLIER &&
        this.selectedAggregateSampleProperty
      ) {
        return (
          'enums.aggregateProperties.' + this.selectedAggregateSampleProperty
        )
      } else if (
        this.selectedSupplierType === referenceTypes.PLANT &&
        this.selectedSamplePropertyId
      ) {
        return 'enums.sampleProperties.' + this.selectedSamplePropertyId
      } else {
        return 'enums.sampleProperties.undefined'
      }
    },
    mixedSampleProperties() {
      if (this.selectedSupplierType === referenceTypes.SUPPLIER) {
        return this.aggregateSampleProperties
      } else if (this.selectedSupplierType === referenceTypes.PLANT) {
        return this.asphaltSampleProperties
      } else {
        return []
      }
    },
    variationTranslationPath() {
      if (this.selectedSupplierType === referenceTypes.SUPPLIER) {
        return 'enums.aggregateProperties'
      } else {
        return 'enums.sampleProperties'
      }
    },
    concreteVariationsData() {
      const formula = this.selectedConcreteFormula
      if (!formula) return []
      return concreteVariations(formula.latest, formula.targets)
    },
    concreteTimeline() {
      const formula = this.selectedConcreteFormula
      if (!formula) return paramTimeline(this.selectedParam, [], {})
      return paramTimeline(this.selectedParam, formula.samples, formula.targets)
    },
  },

  watch: {
    async supplier() {
      await this.initializeSuppliers()
    },
    async mixDesign() {
      if (this.selectedSupplierType === referenceTypes.PLANT) {
        await this.changeMixDesign()
      }
    },
    async specification() {
      if (this.selectedSupplierType === referenceTypes.SUPPLIER) {
        await this.changeAggregate()
      }
    },
    async stockpile() {
      if (this.selectedSupplierType === referenceTypes.SUPPLIER) {
        await this.changeAggregate()
      }
    },
    concreteVariationsData: {
      immediate: true,
      handler(variations) {
        if (
          variations.length &&
          !variations.some((variation) => variation.id === this.selectedParam)
        ) {
          this.selectedParam = variations[0].id
        }
      },
    },
  },

  async created() {
    this.$wait.start('isFetchingDashboard')

    await this.initializeSuppliers()

    this.$wait.end('isFetchingDashboard')
  },

  methods: {
    ...mapMutations('production', [
      'selectAggregateSampleProperty',
      'selectAsphaltSampleProperty',
    ]),
    async initializeSuppliers() {
      if (this.foremanType === referenceTypes.CONCRETE) {
        if (!this.concreteSuppliers.length) {
          await this.$store.dispatch(
            'production/fetchConcreteProductionForemanDashboard',
          )
        }
      } else {
        if (!this.mixedSuppliers.length > 0) {
          await this.$store.dispatch(
            'production/fetchProductionForemanDashboard',
            this.foremanType,
          )
        }
        if (this.foremanType === 'superintendent') {
          if (!this.concreteSuppliers.length) {
            await this.$store.dispatch(
              'production/fetchConcreteProductionForemanDashboard',
            )
          }
        }
      }

      if (this.suppliers.length > 0) {
        await this.$store.commit(
          'production/selectMixedSupplier',
          this.supplier ||
            this.selectedMixedSupplierId ||
            this.suppliers[0]._id,
        )

        if (this.isConcreteSelected) {
          this.$store.commit(
            'production/selectConcreteSupplier',
            this.selectedMixedSupplierId,
          )
          this.setProductionWeather(this.selectedConcreteSupplier)
          this.selectDefaultFormula()
          return
        }

        this.setProductionWeather(this.selectedMixedSupplier)

        if (this.selectedSupplierType === referenceTypes.SUPPLIER) {
          await this.loadAggregates()
          await this.changeAggregate()
        } else if (this.selectedSupplierType === referenceTypes.PLANT) {
          await this.loadMixDesigns()
          await this.changeMixDesign()
        }
      }
    },
    selectDefaultFormula() {
      const list = this.concreteForemanTableList
      const exists = list.some(
        (formula) => formula.id === this.selectedConcreteFormulaId,
      )
      if (!exists) {
        const first = list[0]
        this.$store.commit(
          'production/selectConcreteFormula',
          first ? first.id : '',
        )
      }
    },
    async changeAggregate() {
      if (this.aggregates.length) {
        if (this.specification && this.stockpile) {
          await this.$store.dispatch('production/changeSelectedAggregate', {
            specification: this.specification,
            stockpile: this.stockpile,
          })
        } else if (this.aggregates.length > 0) {
          await this.$store.dispatch('production/changeSelectedAggregate', {
            specification: this.aggregates[0].specification,
            stockpile: this.aggregates[0].stockpile,
          })
        }
      }
    },
    async changeMixDesign() {
      if (this.mixDesigns.length) {
        if (this.mixDesign) {
          await this.$store.dispatch('production/changeSelectedMixDesign', {
            mixDesignId: this.mixDesign,
          })
        } else if (this.asphaltSamples.length > 0) {
          await this.$store.dispatch('production/changeSelectedMixDesign', {
            mixDesignId: this.asphaltSamples[0].mixDesign,
          })
        }
      }
    },
    async loadAggregates() {
      await this.$store.dispatch(
        'production/fetchAggregateProductionTechnicianDashboard',
        {
          supplierId: this.selectedMixedSupplierId,
          includeJobsites: true,
        },
      )
    },
    async loadMixDesigns() {
      await this.$store.dispatch(
        'production/fetchProductionTechnicianDashboard',
        {
          plantId: this.selectedMixedSupplierId,
          includeJobsites: true,
        },
      )
    },
    onMixedSupplierChange(item) {
      this.$router.replace({
        ...this.$router.currentRoute,
        query: { supplier: item },
      })
    },
    onSelectedMaterialChange(item) {
      if (this.selectedSupplierType === referenceTypes.SUPPLIER) {
        this.$router.replace({
          ...this.$router.currentRoute,
          query: {
            supplier: this.supplier,
            specification: item.specification,
            stockpile: item.stockpile,
          },
        })
      } else if (this.selectedSupplierType === referenceTypes.PLANT) {
        this.$router.replace({
          ...this.$router.currentRoute,
          query: { supplier: this.supplier, mixDesign: item.id },
        })
      }
    },
    onPropertyRowClick(data) {
      if (this.selectedSupplierType === referenceTypes.SUPPLIER) {
        this.selectAggregateSampleProperty(data.id)
      } else if (this.selectedSupplierType === referenceTypes.PLANT) {
        if (this.selectedMixDesign.specifications[data.id]) {
          this.selectAsphaltSampleProperty(data.id)
        }
      }
    },
    onFormulaSelected(item) {
      this.$store.commit('production/selectConcreteFormula', item.id)
    },
    onVariationRowClick(event) {
      if (event && event.id) this.selectedParam = event.id
    },
  },
}
</script>
<style scoped>
.supplier-list {
  grid-row: 1 / span 5;
  grid-column: 1 / span 6;
}
.mixed-mix-aggregate-list {
  grid-row: 6 / span 7;
  grid-column: 1 / span 6;
}
.variation-chart {
  grid-row: 1 / span 6;
  grid-column: 7 / span 6;
}
.boundary-chart {
  grid-row: 7 / span 6;
  grid-column: 7 / span 6;
}
</style>
